import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Layout, Button, Collapse, Form, Input, Spin, Row, Col, Divider, message } from 'antd';
import { Prompt } from 'react-router';
import LayoutHeader from '../../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../../common/services/Utils';
import {
    DataCrafterTemplatesVisualStore,
    DataCrafterTemplateFormStore,
    DataCrafterOutputVariantsStore,
    DataCrafterScriptCompilationStore
} from '../../stores';
import { TemplateSubmitOptions } from '../../types';
import { DataCrafterTemplatePickerScript, DataCrafterTemplateScript } from '../index';
import { FileExcelOutlined, CodeOutlined, AppstoreOutlined } from '@ant-design/icons';
import { PackageSelectors, PackageColumnsSelectors, MLModelSelector } from '../index';
import './EditDataCrafterTemplate.less';

type Props = {
    store: DataCrafterTemplatesVisualStore;
    formStore: DataCrafterTemplateFormStore;
    outputVariantsStore: DataCrafterOutputVariantsStore;
    scriptCompilationStore: DataCrafterScriptCompilationStore;
};

const EditDataCrafterTemplate: React.FC<Props> = ({
    store,
    formStore,
    outputVariantsStore,
    scriptCompilationStore
}) => {
    const [form] = Form.useForm();

    React.useEffect(() => {
        return () => {
            formStore.reset();
        };
    }, [formStore]);

    React.useEffect(() => {
        if (store.currentProject && store.selectedTemplateId) {
            formStore.loadPackages();
            formStore.initializeWithTemplate(store.selectedTemplateId);
        }
    }, [store, store.currentProject, store.selectedTemplateId, formStore]);

    // Update form values when template is loaded
    React.useEffect(() => {
        if (formStore.selectedTemplate) {
            form.setFieldsValue({
                templateName: formStore.templateName,
                coderModelId: formStore.selectedCoderModelId
            });
        }
    }, [form, formStore.selectedTemplate, formStore.templateName, formStore.selectedCoderModelId]);

    const handleSubmit = async (options?: TemplateSubmitOptions) => {
        try {
            const values = await form.validateFields();
            return Boolean(await formStore.updateTemplate(values.templateName, values.coderModelId, options));
        } catch (error) {
            // Form validation failed
            message.error('Please fill in all required fields');
            return false;
        }
    };

    const handleUpdate = async () => {
        if (formStore.isBusyWithScriptOperation) {
            return;
        }

        const templateId = formStore.currentTemplateId;
        scriptCompilationStore.setIsPreparingCompilation(templateId, true);

        try {
            await handleSubmit();
        } finally {
            scriptCompilationStore.setIsPreparingCompilation(templateId, false);
        }
    };

    // Handle form field changes with a single handler
    const handleFormValuesChange = (changedValues: Record<string, string | undefined>) => {
        formStore.setFormChanged(true);

        if ('templateName' in changedValues && typeof changedValues.templateName === 'string') {
            formStore.setTemplateName(changedValues.templateName);
        }
        if ('coderModelId' in changedValues) {
            formStore.setSelectedCoderModelId(changedValues.coderModelId);
        }
    };

    return (
        <Layout className="edit-data-crafter-template" style={{ height: '100%', background: 'white' }}>
            <Prompt when={formStore.formChanged} message="You have unsaved changes. Are you sure you want to leave?" />
            <LayoutHeader
                subtitle={Utils.getSubtitle(store.currentProject)}
                helpMessage=""
                title="Edit Data Crafter Template"
                buttons={[
                    <Button
                        key="data-crafter-templates-go-back"
                        data-id="button-go-to-list"
                        className="light"
                        size="large"
                        onClick={() => formStore.navigateToList()}
                        disabled={formStore.isBusyWithScriptOperation}
                    >
                        Go back
                    </Button>,
                    <Button
                        key="data-crafter-templates-submit"
                        data-id="button-submit"
                        type="primary"
                        size="large"
                        onClick={handleUpdate}
                        disabled={
                            !formStore.canSubmit || formStore.isLoadingTemplate || formStore.isBusyWithScriptOperation
                        }
                    >
                        Update Template
                    </Button>
                ]}
            />
            <Layout>
                <Content style={{ maxHeight: 'calc(100vh - 70px)', overflowY: 'auto' }}>
                    <div style={{ padding: '24px' }}>
                        <Spin spinning={formStore.isLoadingTemplate} tip="Loading template...">
                            <Form
                                form={form}
                                layout="vertical"
                                className="data-crafter-template-form"
                                onValuesChange={handleFormValuesChange}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="templateName"
                                            label="Template Name"
                                            rules={[
                                                { required: true, message: 'Please enter a template name' },
                                                { whitespace: true, message: 'Template name cannot be empty' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="Enter template name"
                                                disabled={
                                                    formStore.isLoadingTemplate || formStore.isBusyWithScriptOperation
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="coderModelId"
                                            label="Code Generation Model"
                                            rules={[
                                                { required: true, message: 'Please select a code generation model' }
                                            ]}
                                        >
                                            <MLModelSelector
                                                models={formStore.mlModels}
                                                isLoading={formStore.isLoadingMlModels || formStore.isLoadingTemplate}
                                                placeholder="Select a code generation model"
                                                disabled={
                                                    formStore.isLoadingTemplate || formStore.isBusyWithScriptOperation
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Collapse defaultActiveKey={['1', '2']} bordered={false}>
                                    <Collapse.Panel
                                        header={
                                            <span>
                                                <FileExcelOutlined /> Step 1: Select Package Columns
                                            </span>
                                        }
                                        key="1"
                                        collapsible={formStore.isLoadingTemplate ? 'disabled' : undefined}
                                    >
                                        <Divider className="selectors-divider" />
                                        <PackageSelectors
                                            formStore={formStore}
                                            outputVariantsStore={outputVariantsStore}
                                        />
                                        <Divider className="selectors-divider" />
                                        <PackageColumnsSelectors formStore={formStore} />
                                    </Collapse.Panel>
                                    <Collapse.Panel
                                        header={
                                            <span>
                                                <CodeOutlined /> Step 2: Compile Template Script
                                            </span>
                                        }
                                        key="2"
                                        collapsible={formStore.isLoadingTemplate ? 'disabled' : undefined}
                                    >
                                        <DataCrafterTemplateScript
                                            store={formStore}
                                            scriptCompilationStore={scriptCompilationStore}
                                            handleSubmit={handleSubmit}
                                        />
                                    </Collapse.Panel>
                                    {outputVariantsStore.outputVariants?.length > 0 && (
                                        <Collapse.Panel
                                            header={
                                                <span>
                                                    <AppstoreOutlined /> Step 3: Compile Output Variation Picker Script
                                                </span>
                                            }
                                            key="3"
                                        >
                                            <DataCrafterTemplatePickerScript
                                                store={formStore}
                                                scriptCompilationStore={scriptCompilationStore}
                                            />
                                        </Collapse.Panel>
                                    )}
                                </Collapse>
                            </Form>
                        </Spin>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(EditDataCrafterTemplate);
